import { useMemo, useState } from 'react'
import AgentRanBadge from './AgentRanBadge.jsx'
import { AGENT_LABELS } from '../lib/agents.js'

// Drafts inbox — the screen customers visit each Monday to approve
// the AI-generated posts.
//
// Status tabs: Draft (needs review) | Approved (ready to post) |
//              Posted (history) | Rejected (history)
//
// Each row is a card with the hook + caption + hashtags + (for
// videos) shot list + (for carousels) slide breakdown. Approve
// is one tap. Edit opens an inline editor. Reject prompts for an
// optional note that hints next-cycle generation.
//
// "Mark as posted" is the customer's manual confirmation after
// they paste the caption into Meta Business Suite or TikTok.
// Phase 2 will replace this step with Buffer auto-publishing.
export default function MarketingDraftsInbox({
  drafts,        // raw array (already RLS-scoped)
  grouped,       // { draft, approved, rejected, posted }
  counts,        // counts per status
  loading,
  error,
  onRefresh,
  onApprove,     // (id) => Promise
  onReject,      // (id, note) => Promise
  onEdit,        // (id, patch) => Promise
  onMarkPosted,  // (id) => Promise
  onGenerateNow, // ({ force }) => Promise<number>  — manual trigger
  onOpenSettings,
  hasSettings,   // boolean — false means show the empty/onboarding state
}) {
  const [tab, setTab] = useState('draft')
  const [editing, setEditing] = useState(null) // draft id
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState(null)

  const visible = grouped?.[tab] ?? []

  // Latest generated_at across all drafts — feeds the "Agent ran · …"
  // badge in the header. Drafts arrive newest-first, but we max() across
  // them defensively so badge accuracy never depends on sort order.
  const latestGeneratedAt = useMemo(() => {
    if (!Array.isArray(drafts) || drafts.length === 0) return null
    let best = null
    let bestMs = -Infinity
    for (const d of drafts) {
      const t = d?.generated_at
      if (!t) continue
      const ms = new Date(t).getTime()
      if (Number.isFinite(ms) && ms > bestMs) {
        bestMs = ms
        best = t
      }
    }
    return best
  }, [drafts])

  async function handleGenerateNow(force = false) {
    if (generating || !onGenerateNow) return
    setGenerating(true)
    setGenerateError(null)
    try {
      const count = await onGenerateNow({ force })
      if (count === 0) setGenerateError('No drafts generated. Check that automation is enabled and your menu has data.')
    } catch (e) {
      setGenerateError(e?.message || 'Generation failed.')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-3 px-4 pb-8 pt-4">
      <Header
        counts={counts}
        generatedAt={latestGeneratedAt}
        onOpenSettings={onOpenSettings}
        onRefresh={onRefresh}
      />

      {!hasSettings && <FirstTimeCard onOpenSettings={onOpenSettings} />}

      {hasSettings && (
        <GenerateNowCard
          onGenerate={handleGenerateNow}
          generating={generating}
          error={generateError}
          existingDraftCount={counts?.draft || 0}
        />
      )}

      <Tabs tab={tab} onChange={setTab} counts={counts} />

      {loading && (
        <div className="rounded-lg bg-black/5 px-3 py-6 text-center text-sm text-black/55">
          Loading drafts…
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white">
          Could not load drafts: {error?.message || 'try refreshing.'}
        </div>
      )}

      {!loading && visible.length === 0 && hasSettings && (
        <EmptyState tab={tab} />
      )}

      {visible.map((d) =>
        editing === d.id
          ? (
            <DraftEditor
              key={d.id}
              draft={d}
              onSave={async (patch) => { await onEdit(d.id, patch); setEditing(null) }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <DraftCard
              key={d.id}
              draft={d}
              onApprove={() => onApprove(d.id)}
              onReject={(note) => onReject(d.id, note)}
              onEdit={() => setEditing(d.id)}
              onMarkPosted={() => onMarkPosted(d.id)}
            />
          )
      )}
    </div>
  )
}

// ---------- subcomponents ----------

function Header({ counts, generatedAt, onOpenSettings, onRefresh }) {
  const draftCount = counts?.draft || 0
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-medium">
            {AGENT_LABELS.marketingDrafts} — {draftCount} draft{draftCount === 1 ? '' : 's'} ready
          </h2>
          <AgentRanBadge generatedAt={generatedAt} tone="light" />
        </div>
        <p className="text-xs text-black/55">
          {draftCount} waiting · {counts?.approved || 0} approved · {counts?.posted || 0} posted
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRefresh}
          className="rounded-lg border border-black/15 px-3 py-1.5 text-sm font-medium text-black/70 hover:bg-black/5"
        >
          Refresh
        </button>
        <button
          type="button"
          onClick={onOpenSettings}
          className="rounded-lg border border-black/15 px-3 py-1.5 text-sm font-medium text-black/70 hover:bg-black/5"
        >
          Settings
        </button>
      </div>
    </div>
  )
}

function GenerateNowCard({ onGenerate, generating, error, existingDraftCount }) {
  // Subtle, not loud — most weeks the customer will rely on the
  // weekly cron and ignore this card. We surface "Generate fresh"
  // only when there are already drafts, so first-time-each-week
  // it just says "Generate this week's drafts now."
  const hasDrafts = existingDraftCount > 0
  return (
    <div className="rounded-lg border border-black/10 bg-black/[0.02] px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-black/65">
          {hasDrafts
            ? `Need a fresh batch? Replace this week's ${existingDraftCount} draft${existingDraftCount === 1 ? '' : 's'} with new ones.`
            : `Don't want to wait until next cycle? Generate this week's drafts now.`}
        </div>
        <button
          type="button"
          onClick={() => onGenerate(hasDrafts)}
          disabled={generating}
          className="flex-shrink-0 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {generating ? 'Generating…' : (hasDrafts ? 'Generate fresh' : 'Generate now')}
        </button>
      </div>
      {error && (
        <div className="mt-2 rounded bg-red-100 px-2 py-1 text-xs text-red-800">
          {error}
        </div>
      )}
    </div>
  )
}

function FirstTimeCard({ onOpenSettings }) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3">
      <div className="font-medium text-blue-900">Marketing automation is off</div>
      <p className="mt-1 text-sm text-blue-900/80">
        Turn it on and we'll draft 5 ready-to-post social media posts for you every week,
        using your real menu items and current promotions.
      </p>
      <button
        type="button"
        onClick={onOpenSettings}
        className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Enable automation
      </button>
    </div>
  )
}

function Tabs({ tab, onChange, counts }) {
  const items = [
    { id: 'draft',    label: 'Needs review', count: counts?.draft },
    { id: 'approved', label: 'Approved',     count: counts?.approved },
    { id: 'posted',   label: 'Posted',       count: counts?.posted },
    { id: 'rejected', label: 'Rejected',     count: counts?.rejected },
  ]
  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-black/10 bg-black/[0.02] p-1">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className={`flex-1 rounded-md px-2 py-2 text-xs font-medium ${
            tab === it.id
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-black/55 hover:text-black/80'
          }`}
        >
          {it.label}
          {typeof it.count === 'number' && it.count > 0 && (
            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${
              tab === it.id ? 'bg-blue-100 text-blue-700' : 'bg-black/10 text-black/60'
            }`}>{it.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

function EmptyState({ tab }) {
  const messages = {
    draft:    "All clear. Next batch arrives on your scheduled day.",
    approved: "Nothing approved yet. Approve drafts above to queue them here.",
    posted:   "Nothing posted yet. After publishing on IG/TikTok, hit \"Mark posted\" to file the draft here.",
    rejected: "Nothing rejected yet.",
  }
  return (
    <div className="rounded-lg bg-black/5 px-3 py-6 text-center text-sm text-black/55">
      {messages[tab]}
    </div>
  )
}

function DraftCard({ draft, onApprove, onReject, onEdit, onMarkPosted }) {
  const [busy, setBusy] = useState(false)
  const [showRejectPrompt, setShowRejectPrompt] = useState(false)
  const [rejectNote, setRejectNote] = useState('')

  async function wrap(fn) {
    if (busy) return
    setBusy(true)
    try { await fn() } finally { setBusy(false) }
  }

  return (
    <div className="rounded-2xl border border-black/10 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-black/5 px-3 py-2">
        <div className="flex items-center gap-2">
          <PlatformBadge platform={draft.platform} />
          <PostTypeBadge postType={draft.post_type} />
        </div>
        <span className="text-[11px] text-black/45">
          {formatRelative(draft.generated_at)}
        </span>
      </div>

      <div className="space-y-3 px-3 py-3">
        <div>
          <div className="text-[10px] font-medium text-black/45">Hook</div>
          <div className="text-sm font-medium">{draft.hook}</div>
        </div>

        <div>
          <div className="text-[10px] font-medium text-black/45">Caption</div>
          <div className="whitespace-pre-line text-sm text-black/85">{draft.caption}</div>
        </div>

        {Array.isArray(draft.hashtags) && draft.hashtags.length > 0 && (
          <div>
            <div className="text-[10px] font-medium text-black/45">Hashtags</div>
            <div className="text-sm text-blue-700">
              {draft.hashtags.map((h) => h.startsWith('#') ? h : `#${h}`).join(' ')}
            </div>
          </div>
        )}

        {Array.isArray(draft.shot_list) && draft.shot_list.length > 0 && (
          <details className="rounded-lg bg-black/[0.03] px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-black/70">
              Shot list ({draft.shot_list.length} shots)
            </summary>
            <ol className="mt-2 space-y-2 text-xs text-black/70">
              {draft.shot_list.map((s, i) => (
                <li key={i}>
                  <strong className="text-black">{s.ts}</strong> — {s.shot}
                  <div className="text-black/55 italic">"{s.voice}"</div>
                </li>
              ))}
            </ol>
          </details>
        )}

        {Array.isArray(draft.slides) && draft.slides.length > 0 && (
          <details className="rounded-lg bg-black/[0.03] px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-black/70">
              Carousel slides ({draft.slides.length})
            </summary>
            <ol className="mt-2 space-y-2 text-xs text-black/70">
              {draft.slides.map((s) => (
                <li key={s.slide}>
                  <strong className="text-black">Slide {s.slide}: {s.headline}</strong>
                  <div>{s.body}</div>
                  <div className="italic text-black/55">Image idea: {s.image_idea}</div>
                </li>
              ))}
            </ol>
          </details>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-black/5 px-3 py-2">
        {draft.status === 'draft' && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => wrap(onApprove)}
              className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onEdit}
              className="rounded-lg border border-black/15 px-3 py-2 text-sm font-medium text-black/70 hover:bg-black/5 disabled:opacity-50"
            >
              Edit
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowRejectPrompt(true)}
              className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Reject
            </button>
          </>
        )}
        {draft.status === 'approved' && (
          <>
            <CopyCaptionButton caption={composeForCopy(draft)} />
            <button
              type="button"
              disabled={busy}
              onClick={() => wrap(onMarkPosted)}
              className="rounded-lg border border-black/15 px-3 py-2 text-sm font-medium text-black/70 hover:bg-black/5 disabled:opacity-50"
            >
              Mark posted
            </button>
          </>
        )}
        {draft.status === 'posted' && (
          <span className="text-xs text-black/55">
            Posted {formatRelative(draft.posted_at)}
          </span>
        )}
        {draft.status === 'rejected' && (
          <span className="text-xs text-black/55">
            {draft.rejection_note ? `Rejected: ${draft.rejection_note}` : 'Rejected'}
          </span>
        )}
      </div>

      {showRejectPrompt && (
        <div className="border-t border-black/5 bg-red-50/40 px-3 py-3 space-y-2">
          <label className="block text-xs font-medium text-red-900">
            Tell us why so the next batch is better (optional):
          </label>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g., we don't talk about competitors / wrong tone / promotion already ended"
            rows={2}
            className="w-full rounded border border-red-200 bg-white px-2 py-1 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => wrap(async () => { await onReject(rejectNote.trim() || null); setShowRejectPrompt(false) })}
              disabled={busy}
              className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Confirm reject
            </button>
            <button
              type="button"
              onClick={() => setShowRejectPrompt(false)}
              className="rounded-lg border border-black/15 px-3 py-2 text-sm font-medium text-black/70 hover:bg-black/5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function DraftEditor({ draft, onSave, onCancel }) {
  const [hook, setHook]         = useState(draft.hook)
  const [caption, setCaption]   = useState(draft.caption)
  const [tags, setTags]         = useState((draft.hashtags || []).join(' '))
  const [submitting, setSubmitting] = useState(false)

  async function handleSave() {
    if (submitting) return
    setSubmitting(true)
    try {
      await onSave({
        hook: hook.trim() || draft.hook,
        caption: caption.trim() || draft.caption,
        hashtags: tags.split(/\s+/).map((t) => t.trim()).filter(Boolean),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-2xl border border-blue-300 bg-white shadow-sm">
      <div className="border-b border-black/5 px-3 py-2 text-sm font-medium">
        Editing draft · {draft.platform} · {draft.post_type}
      </div>
      <div className="space-y-3 px-3 py-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-black/55">Hook</label>
          <input
            value={hook}
            onChange={(e) => setHook(e.target.value)}
            className="w-full rounded border border-black/15 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-black/55">Caption</label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={5}
            className="w-full rounded border border-black/15 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-black/55">Hashtags (space-separated)</label>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full rounded border border-black/15 px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2 border-t border-black/5 px-3 py-2">
        <button
          type="button"
          disabled={submitting}
          onClick={handleSave}
          className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-black/15 px-3 py-2 text-sm font-medium text-black/70 hover:bg-black/5"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function CopyCaptionButton({ caption }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(caption)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          /* clipboard might be blocked; ignore — UI still works */
        }
      }}
      className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
    >
      {copied ? 'Copied!' : 'Copy caption'}
    </button>
  )
}

// ---------- helpers ----------

function PlatformBadge({ platform }) {
  const colors = {
    instagram: 'bg-pink-100 text-pink-800',
    tiktok:    'bg-black text-white',
    facebook:  'bg-blue-100 text-blue-800',
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${colors[platform] || 'bg-black/10'}`}>
      {platform}
    </span>
  )
}

function PostTypeBadge({ postType }) {
  return (
    <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-medium text-black/70">
      {postType}
    </span>
  )
}

function composeForCopy(draft) {
  const tags = Array.isArray(draft.hashtags) && draft.hashtags.length
    ? '\n\n' + draft.hashtags.map((h) => h.startsWith('#') ? h : `#${h}`).join(' ')
    : ''
  return `${draft.caption}${tags}`
}

function formatRelative(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}
